import { useCallback, useEffect, useRef } from "react";
import type { DragMoveEvent } from "@dnd-kit/core";
import { getHoverCellPlacement, getHoverTarget } from "./boardDragHover";
import {
    getDropOrder,
    type BoardDraggingState,
    type BoardDragState,
} from "./boardDragState";
import type { BoardMoveTo } from "./boardMove";
import { isAfterCollision } from "./boardSensors";
import type { LatestBoardDragArgs } from "./useBoardDragStore";
import { useBoardPlaceModifier } from "./useBoardPlaceModifier";

/** What a pointer hover resolves from: the same inputs give the same target. */
interface BoardHover<TItem> {
    model: LatestBoardDragArgs<TItem>;
    current: BoardDraggingState;
    overId: string;
    isAfter: boolean;
}

const getTarget = <TItem>({
    model,
    current,
    overId,
    isAfter,
}: BoardHover<TItem>) =>
    getHoverTarget({
        model,
        current,
        overId,
        cellPlacement: getHoverCellPlacement(model, { current, overId }),
        isAfter,
    });

// The copy follows the pointer unless a sorted board places it; there it
// sits where the order puts it, and hovering it targets the cell it is in.
const isOverPaintedCopy = <TItem>({
    model,
    current,
    overId,
}: BoardHover<TItem>) =>
    overId === current.activeKey && getDropOrder(model, current) === undefined;

const isSameHover = <TItem>(
    previous: BoardHover<TItem> | null,
    next: BoardHover<TItem>,
) =>
    previous !== null &&
    previous.model === next.model &&
    previous.current === next.current &&
    previous.overId === next.overId &&
    previous.isAfter === next.isAfter;

interface UseBoardPointerHoverArgs<TItem> {
    stateRef: { readonly current: BoardDragState<TItem> };
    latestRef: { readonly current: LatestBoardDragArgs<TItem> };
    moveTo: (current: BoardDraggingState, step: BoardMoveTo) => void;
    commit: (next: BoardDragState<TItem>) => void;
    isPointerDrag: boolean;
}

/**
 * dnd-kit's move and over events during a pointer drag: the hovered card
 * (above or below its middle), cell or collapsed strip becomes the target.
 * Moves within one target are skipped: resolving it rebuilds the preview.
 * When the place modifier goes down or up without a move, the last hover of
 * the current drag is resolved again for the new state.
 */
export const useBoardPointerHover = <TItem>({
    stateRef,
    latestRef,
    moveTo,
    commit,
    isPointerDrag,
}: UseBoardPointerHoverArgs<TItem>) => {
    const lastHoverRef = useRef<BoardHover<TItem> | null>(null);
    useEffect(() => {
        if (!isPointerDrag) {
            lastHoverRef.current = null;
        }
    }, [isPointerDrag]);

    const onDragHover = useCallback(
        ({ over, collisions }: DragMoveEvent) => {
            const current = stateRef.current;
            if (
                current.phase !== "dragging" ||
                current.isKeyboard ||
                over === null
            ) {
                return;
            }
            const hover: BoardHover<TItem> = {
                model: latestRef.current,
                current,
                overId: String(over.id),
                isAfter: isAfterCollision(collisions?.[0]),
            };
            if (
                isOverPaintedCopy(hover) ||
                isSameHover(lastHoverRef.current, hover)
            ) {
                return;
            }
            lastHoverRef.current = hover;
            moveTo(
                { ...current, isSlotInstant: false },
                { target: getTarget(hover) },
            );
        },
        [latestRef, moveTo, stateRef],
    );

    const replayHover = useCallback(
        (current: BoardDraggingState) => {
            const last = lastHoverRef.current;
            if (last === null || last.current.activeKey !== current.activeKey) {
                return;
            }
            const hover = { ...last, model: latestRef.current, current };
            lastHoverRef.current = hover;
            if (!isOverPaintedCopy(hover)) {
                moveTo(current, { target: getTarget(hover) });
            }
        },
        [latestRef, moveTo],
    );

    useBoardPlaceModifier({
        stateRef,
        latestRef,
        commit,
        replayHover,
        isPointerDrag,
    });

    return onDragHover;
};
